import type { ReactNode } from "react";
import {
  ISSUE_STAGES,
  type IssueSeverity,
  type IssueStage,
  type ScheduleValidationIssue,
} from "../types";

interface IssuesCardProps {
  issues: ScheduleValidationIssue[];
  /** Custom empty-state node; defaults to a muted "No issues found." line. */
  emptyState?: ReactNode;
  className?: string;
}

/** Human-readable label per backend `IssueStage` enum value. */
const STAGE_LABELS: Record<IssueStage, string> = {
  CalendarImport: "Calendar import",
  CourseRequestsImport: "Course requests",
  PostRun: "Post-run",
};

/**
 * Display order for stage sections — earlier stages render first regardless of
 * which issues exist. Stages with zero issues are omitted from the rendered
 * tree (see `groupedStages` below).
 *
 * Aliased to `ISSUE_STAGES` (the type's source-of-truth tuple) so adding a new
 * stage to `IssueStage` automatically appends to the render order — no second
 * spot to update, no `STAGE_ORDER` drifting silently from `IssueStage`.
 */
const STAGE_ORDER = ISSUE_STAGES;

interface GroupedCode {
  code: string;
  issues: ScheduleValidationIssue[];
}

interface GroupedStage {
  stage: IssueStage;
  codes: GroupedCode[];
}

/**
 * Group flat issues into `{ stage -> code -> issues[] }`. Stage order is
 * stable per {@link STAGE_ORDER}; within a stage, codes appear in first-seen
 * order (matches backend listing order). Pure helper kept module-level so it
 * can be unit-tested separately if needed and avoids re-allocating per render.
 */
function groupIssues(issues: ScheduleValidationIssue[]): GroupedStage[] {
  const byStage = new Map<IssueStage, Map<string, ScheduleValidationIssue[]>>();
  for (const issue of issues) {
    let codeMap = byStage.get(issue.stage);
    if (!codeMap) {
      codeMap = new Map();
      byStage.set(issue.stage, codeMap);
    }
    const bucket = codeMap.get(issue.code);
    if (bucket) {
      bucket.push(issue);
    } else {
      codeMap.set(issue.code, [issue]);
    }
  }
  const result: GroupedStage[] = [];
  for (const stage of STAGE_ORDER) {
    const codeMap = byStage.get(stage);
    if (!codeMap) continue;
    const codes: GroupedCode[] = [];
    for (const [code, codeIssues] of codeMap) {
      codes.push({ code, issues: codeIssues });
    }
    result.push({ stage, codes });
  }
  return result;
}

/**
 * SR-only severity prefix used to announce the severity before the message
 * text. Kept as an exhaustive switch so adding a new severity (`"Info"`, …)
 * fails the build until a prefix is provided here too.
 */
function severityPrefix(severity: IssueSeverity): string {
  switch (severity) {
    case "Error":
      return "Error: ";
    case "Warning":
      return "Warning: ";
    default: {
      const _exhaustive: never = severity;
      return _exhaustive;
    }
  }
}

interface SeverityIconProps {
  severity: IssueSeverity;
}

/**
 * Inline SVG severity indicator. Error renders a filled red circle with a
 * white exclamation; Warning renders an amber triangle. Exhaustive on
 * {@link IssueSeverity} — adding a new severity is a compile error.
 */
function SeverityIcon({ severity }: SeverityIconProps) {
  switch (severity) {
    case "Error":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          aria-hidden="true"
          data-testid="severity-icon-error"
          className="text-red-400 shrink-0"
        >
          <circle cx="12" cy="12" r="10" fill="currentColor" />
          {/* Inner glyph uses `currentColor` against the dark page background
              by way of `text-page`, so high-contrast / themed UIs render the
              exclamation in the active foreground rather than a fixed white. */}
          <g className="text-page">
            <path
              d="M12 7v6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="12" cy="16.5" r="1.2" fill="currentColor" />
          </g>
        </svg>
      );
    case "Warning":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          aria-hidden="true"
          data-testid="severity-icon-warning"
          className="text-amber-400 shrink-0"
        >
          <path d="M12 3 22 20H2L12 3Z" fill="currentColor" />
          {/* Same trick as the Error case: inner glyph rides on a sibling
              `text-page` class so the exclamation stays legible across themes
              without hardcoding `#1f2937`. */}
          <g className="text-page">
            <path
              d="M12 10v4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="12" cy="17" r="1.1" fill="currentColor" />
          </g>
        </svg>
      );
    default: {
      const _exhaustive: never = severity;
      return _exhaustive;
    }
  }
}

/**
 * Read-only presentation of scheduler validation issues, grouped by stage
 * then by issue code. Severity counts are surfaced in a top-line summary.
 * Pure presentation: callers are responsible for fetching the issue list
 * (see {@link WizardStepReview}).
 *
 * IMPORTANT: callers MUST gate rendering on a settled query state
 * (`isSuccess && !isLoading && !isError`). The empty-state copy explicitly
 * reads as "the calendar parsed cleanly" — rendering this component during a
 * loading or errored state would mislead users into thinking the data is
 * settled when it isn't. The wizard / schedule home both follow this rule.
 */
export function IssuesCard({ issues, emptyState, className }: IssuesCardProps) {
  if (issues.length === 0) {
    return (
      <div
        data-testid="issues-card-empty"
        className={`bg-surface border border-border-subtle rounded-lg p-6 text-sm text-text-muted ${className ?? ""}`.trim()}
      >
        {emptyState ?? "No issues found — your calendar parsed cleanly."}
      </div>
    );
  }

  const errorCount = issues.filter((i) => i.severity === "Error").length;
  const warningCount = issues.length - errorCount;
  const grouped = groupIssues(issues);

  return (
    <div
      data-testid="issues-card"
      className={`bg-surface border border-border-subtle rounded-lg ${className ?? ""}`.trim()}
    >
      <div
        data-testid="issues-summary"
        className="px-5 py-3 border-b border-border-subtle flex items-center gap-4 text-[13px]"
      >
        <span
          data-testid="issues-summary-errors"
          className="text-red-400 font-medium"
        >
          {errorCount} {errorCount === 1 ? "error" : "errors"}
        </span>
        <span
          data-testid="issues-summary-warnings"
          className="text-amber-400 font-medium"
        >
          {warningCount} {warningCount === 1 ? "warning" : "warnings"}
        </span>
      </div>

      <div className="divide-y divide-border-subtle">
        {grouped.map(({ stage, codes }) => (
          <section
            key={stage}
            data-testid={`issues-stage-${stage}`}
            className="px-5 py-4"
          >
            <h3 className="text-xs uppercase tracking-wide text-text-muted font-medium mb-3">
              {STAGE_LABELS[stage]}
            </h3>
            <div className="space-y-4">
              {codes.map(({ code, issues: codeIssues }) => (
                <div
                  key={code}
                  data-testid={`issues-code-${stage}-${code}`}
                  className="space-y-1.5"
                >
                  <div className="text-[13px] font-semibold text-white">
                    {code}
                  </div>
                  <ul className="space-y-1.5">
                    {codeIssues.map((issue) => (
                      <li
                        key={issue.id}
                        data-testid={`issue-row-${issue.id}`}
                        className="flex items-start gap-2 text-[13px] text-text-soft"
                      >
                        <span className="pt-0.5">
                          <SeverityIcon severity={issue.severity} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <span className="sr-only">
                            {severityPrefix(issue.severity)}
                          </span>
                          <span>{issue.message}</span>
                          {issue.eventId && (
                            <span
                              data-testid={`issue-event-chip-${issue.id}`}
                              className="ml-2 inline-block px-1.5 py-0.5 rounded bg-surface-2 text-text-faint text-[11px] font-mono"
                            >
                              event {issue.eventId.slice(0, 8)}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
